/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { MerchantDetailsParentComponent } from "../merchant-details-parent.component";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService } from "../../../services";
import { CreditCardEnum, Merchant } from "../../../models/merchant";

import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CREDIT_CARD_CONSTANTS, PATTERN_CONSTANTS } from "../../../utils/data.rest.constants";
import { midLowerThanQualified, nonQualifiedLowerThanMidQualified } from "../../../validators/MPCustomValidator";

@Component({
  selector: "app-rates-and-contract",
  templateUrl: "./rates-and-contract.component.html"
})
export class RatesAndContractComponent extends MerchantDetailsParentComponent implements OnInit {
  ratesFormGroup!: FormGroup;
  editable = false;
  buttonName = "Update Rates and Contract Information";
  actionInProgress = false;
  @Input("merchant") merchant!: Merchant;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.ratesFormGroup = this.fb.group(
      {
        perTransAuthFee: [
          {
            value: this.merchant.contractedRates.perTransAuthFee
              ? this.merchant.contractedRates.perTransAuthFee / 100
              : 0.3,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [
            Validators.min(1 / 1000),
            Validators.max(999999.99),
            Validators.pattern(PATTERN_CONSTANTS.MONEY),
            Validators.required
          ]
        ],
        perTransRefundFee: [
          {
            value: this.merchant.contractedRates.perTransRefundFee
              ? this.merchant.contractedRates.perTransRefundFee / 100
              : 0.3,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [
            Validators.min(1 / 1000),
            Validators.max(999999.99),
            Validators.pattern(PATTERN_CONSTANTS.MONEY),
            Validators.required
          ]
        ],
        perTransECheckFee: [
          {
            value: this.merchant.contractedRates.perTransECheckFee
              ? this.merchant.contractedRates.perTransECheckFee / 100
              : 0.3,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [
            Validators.min(1 / 1000),
            Validators.max(999999.99),
            Validators.pattern(PATTERN_CONSTANTS.MONEY),
            Validators.required
          ]
        ],
        qualifiedUpperBoundaryPercent: [
          {
            value: this.merchant.contractedRates.qualifiedUpperBoundaryPercent
              ? this.merchant.contractedRates.qualifiedUpperBoundaryPercent
              : 1.49,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [Validators.min(0.001), Validators.max(1.989), Validators.required]
        ],
        qualifiedFeePercent: [
          {
            value: this.merchant.contractedRates.qualifiedFeePercent,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [
            Validators.min(0.001),
            Validators.max(100),
            Validators.required,
            Validators.pattern(PATTERN_CONSTANTS.PERCENT_3_DECIMALS)
          ]
        ],
        midQualifiedUpperBoundaryPercent: [
          {
            value: this.merchant.contractedRates.midQualifiedUpperBoundaryPercent
              ? this.merchant.contractedRates.midQualifiedUpperBoundaryPercent
              : 2.49,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [Validators.max(2.989), Validators.required]
        ],
        midQualifiedFeePercent: [
          {
            value: this.merchant.contractedRates.midQualifiedFeePercent,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [
            Validators.min(0.001),
            Validators.max(100),
            Validators.required,
            Validators.pattern(PATTERN_CONSTANTS.PERCENT_3_DECIMALS)
          ]
        ],
        nonQualifiedUpperBoundaryPercent: [
          {
            value: this.merchant.contractedRates.nonQualifiedUpperBoundaryPercent
              ? this.merchant.contractedRates.nonQualifiedUpperBoundaryPercent
              : 100,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [Validators.max(100), Validators.required]
        ],
        nonQualifiedFeePercent: [
          {
            value: this.merchant.contractedRates.nonQualifiedFeePercent,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [
            Validators.min(0.001),
            Validators.max(100),
            Validators.required,
            Validators.pattern(PATTERN_CONSTANTS.PERCENT_3_DECIMALS)
          ]
        ],
        amexPercent: [
          {
            value: this.merchant.contractedRates.amexPercent,
            disabled: !this.authService.canUserAccess("FINANCE")
          },
          [Validators.min(0.001), Validators.max(100), Validators.pattern(PATTERN_CONSTANTS.PERCENT_2_DECIMALS)]
        ],
        suppressFeeSettlement: [
          {
            value: this.merchant.contractedRates.suppressFeeSettlement,
            disabled: !this.authService.canUserAccess("FINANCE")
          }
        ]
      },
      {
        validators: [
          midLowerThanQualified("qualifiedUpperBoundaryPercent", "midQualifiedUpperBoundaryPercent"),
          nonQualifiedLowerThanMidQualified("midQualifiedUpperBoundaryPercent", "nonQualifiedUpperBoundaryPercent")
        ]
      }
    );
    if (this.isAmexFeeRequired()) {
      this.ratesFormGroup.get("amexPercent")?.addValidators(Validators.required);
    } else {
      this.ratesFormGroup.get("amexPercent")?.removeValidators(Validators.required);
    }
  }

  saveRates(): void {
    if (this.ratesFormGroup.valid) {
      this.actionInProgress = true;
      const tempContractedRates = JSON.parse(JSON.stringify(this.merchant.contractedRates));
      tempContractedRates.perTransAuthFee = this.ratesFormGroup.controls.perTransAuthFee.value * 100;
      tempContractedRates.perTransRefundFee = this.ratesFormGroup.controls.perTransRefundFee.value * 100;
      tempContractedRates.perTransECheckFee = this.ratesFormGroup.controls.perTransECheckFee.value * 100;
      tempContractedRates.qualifiedUpperBoundaryPercent =
        this.ratesFormGroup.controls.qualifiedUpperBoundaryPercent.value;
      tempContractedRates.qualifiedFeePercent = this.ratesFormGroup.controls.qualifiedFeePercent.value;
      tempContractedRates.midQualifiedUpperBoundaryPercent =
        this.ratesFormGroup.controls.midQualifiedUpperBoundaryPercent.value;
      tempContractedRates.midQualifiedFeePercent = this.ratesFormGroup.controls.midQualifiedFeePercent.value;
      tempContractedRates.nonQualifiedUpperBoundaryPercent =
        this.ratesFormGroup.controls.nonQualifiedUpperBoundaryPercent.value;
      tempContractedRates.nonQualifiedFeePercent = this.ratesFormGroup.controls.nonQualifiedFeePercent.value;
      tempContractedRates.amexPercent = this.ratesFormGroup.controls.amexPercent.value;
      tempContractedRates.suppressFeeSettlement = this.ratesFormGroup.controls.suppressFeeSettlement.value;
      this.merchantService.setContractedRates(this.merchant.id, tempContractedRates).subscribe(
        (contractedRates) => {
          this.merchant.contractedRates = contractedRates;
          this.merchantService.merchant = this.merchant;
          this.editable = false;
          this.actionInProgress = false;
        },
        (error) => {
          this.notificationService.error(error.error);
          this.actionInProgress = false;
        }
      );
    } else {
      this.ratesFormGroup.markAllAsTouched();
      this.actionInProgress = false;
    }
  }

  isAmexFeeRequired(): boolean {
    const merchant = this.merchantService.merchant;
    const isAmexCard = merchant.acceptedCreditCards.includes(
      CREDIT_CARD_CONSTANTS.CREDIT_CARDS.AMEX.code as CreditCardEnum
    );
    if (isAmexCard) {
      this.ratesFormGroup.get("amexPercent")?.addValidators([Validators.required]);
      this.ratesFormGroup.get("amexPercent")?.updateValueAndValidity();
    } else {
      this.ratesFormGroup.get("amexPercent")?.removeValidators([Validators.required]);
      this.ratesFormGroup.get("amexPercent")?.updateValueAndValidity();
    }
    return (this.isInstaMed() || this.isWorldPay()) && isAmexCard;
  }

  hasContractedRates(): boolean {
    return this.merchant.contractedRates === null ? false : true;
  }

  receiveEditEvent(value: boolean) {
    this.editable = value;
    this.createForm();
  }
}
